// ─────────────────────────────────────────────────────────────────────────
// buildCostControlPdfModel(reportData, options)
//
// Transforms the SAME reportData object produced by CostControl.jsx's own
// buildReport() — the Single Source of Truth for the Cost Control screen —
// into a printable model for the PDF/HTML export. This file NEVER calls
// buildCumulativeData(), buildCostControlDataFromRelease(),
// resolveCurrentForecastState(), or Supabase. It only reads the reportData
// object handed to it, exactly as buildReport() produced it.
//
// CONTRACT (unchanged from the original, explicitly agreed):
//   - Zero financial calculation. Every number in the returned model is
//     read directly from reportData — never summed, subtracted, or
//     derived here beyond simple field selection / re-grouping for
//     display. If a figure isn't already present on reportData, it does
//     not appear in the model.
//   - No branching on data source (Live vs Release). This function only
//     reads whatever options.dataSourceLabel / options.releaseInfo the
//     caller supplies for display purposes — it has no opinion on where
//     reportData came from.
//   - Section filtering reuses the EXACT same grouping reportData itself
//     already carries: resourceRows/resource_type for Direct tabs,
//     reportData.indirectRows / indirectCategories / uncategorizedIndirectRows
//     for Indirect — not a re-invented filter.
//
// SECTIONS ARE DYNAMIC (per agreed requirement — do NOT hardcode a fixed
// list of 4 Direct types). options.sections, when provided, is a list of
// section keys to include, in order. When options.sections is omitted (or
// options.allSections === true), the model auto-discovers every section
// present in reportData:
//   - Material / Labour / Subcontract / Equipment — ONLY the resource
//     types actually found in reportData.directRows[].resourceRows[].
//     If a project has a 5th resource_type value, it appears automatically;
//     if it has no Equipment at all, no empty Equipment section is built.
//   - Indirect — either reportData.indirectCategories (real, user-defined
//     categories, e.g. "Equipment & Transportation") as separate sections,
//     plus an "Uncategorized" section if reportData.uncategorizedIndirectRows
//     has any rows; OR, if the project has defined zero categories, a single
//     "Indirect Works" section from reportData.indirectRows (unchanged
//     backward-compatible behavior, matching the screen's own fallback).
//   - Summary — always appended last: a project-wide Direct/Indirect/Grand
//     Total roll-up, sourced from reportData.directTotals/indirectTotals/
//     grandTotals/resourceTypeTotals exactly as the screen's own Summary
//     tab already reads them.
//
// SECTION KEYS: 'material' | 'labour' | 'subcontract' | 'equipment' |
// 'indirect' | 'cat:<categoryId>' | 'cat:uncategorized' | 'summary'
// (identical keys to CostControl.jsx's own directResourceTab state, plus
// 'summary' for the always-last roll-up section).

const RESOURCE_TYPE_LABELS = {
  material: 'Material', labour: 'Labour', subcontract: 'Subcontract', equipment: 'Equipment',
}
const RESOURCE_TYPE_ORDER = ['material', 'labour', 'subcontract', 'equipment']

const SUM_FIELDS = ['bac', 'commitment', 'actualTotal', 'totalActualCost', 'eac', 'prevEac', 'accrual', 'earnedValue']

function emptySumTotals() {
  const t = {}
  SUM_FIELDS.forEach(k => { t[k] = 0 })
  return t
}

// Only the fields the PDF table actually prints — read as-is, never
// recomputed. Missing fields simply render as 0/blank in the renderer,
// never fabricated here.
//
// actualThisPeriod — per explicit requirement, this is NOT reportData's
// own actualThisPeriod (a "same calendar month/year selected on screen"
// figure from buildCostControlData.js). It is Movement — the same
// period-over-period figure Executive Report shows ("last two periods
// present in the data", not the selected calendar month) — read from
// monthlyActualCost (buildMonthlyActualCostData()'s output), matched by
// this row's own `code`. This is a single shared source of truth with
// Executive Report: no new calculation happens here, only a lookup by
// code into the exact same movement figures that module already computed.
// Falls back to 0 (not the old actualThisPeriod) if monthlyActualCost
// wasn't supplied or the code isn't found there, so the two report
// layers can never silently diverge back onto two different definitions.
function pickDisplayFields(r, monthlyActualCost) {
  // Real Cost Code/Resource/Sub Code rows always go through the movement
  // lookup by their own `code` (even an empty-string code like the
  // Summary section's roll-up rows use — see below). A pre-aggregated
  // totals object (built by summing each row's own Movement in
  // buildDirectResourceSection/buildIndirectSection/buildSummarySection)
  // is marked explicitly with r._isAggregateTotal so its already-computed
  // actualThisPeriod is trusted as-is, rather than relying on a fragile
  // "code is null" check — several aggregate rows below intentionally use
  // code: '' (an empty string, not null/undefined), which would otherwise
  // fall through to a lookup that always misses and returns 0.
  const movementLookup = r._isAggregateTotal
    ? (r.actualThisPeriod || 0)
    : (monthlyActualCost ? (monthlyActualCost.bySubCode?.[r.code]?.movement ?? monthlyActualCost.byResourceCode?.[r.code]?.movement ?? monthlyActualCost.byCostCode?.[r.code]?.movement ?? 0) : 0)
  return {
    bac: r.bac || 0,
    commitment: r.commitment || 0,
    actualTotal: r.actualTotal || 0,
    accrual: r.accrual || 0,
    totalActualCost: r.totalActualCost || 0,
    actualPrevious: r.actualPrevious || 0,
    actualThisPeriod: movementLookup,
    cashOut: r.cashOut || 0,
    eac: r.eac || 0,
    varianceEac: r.varianceEac || 0,
    varianceCompletion: (r.bac || 0) - (r.eac || 0),
    cpi: r.cpi || 0,
    // earnedValue — read as-is from reportData/buildCumulativeData(), the
    // exact same field Executive Report's `g.earnedValue`/`r.ev` (Cost
    // Summary table) reads. Never computed here.
    earnedValue: r.earnedValue || 0,
    // costVariance — standard EVM formula (Cost Variance = Earned Value -
    // Actual Cost), the same class of simple two-field subtraction as the
    // existing varianceCompletion (bac - eac) above — not a new financial
    // rule, no different aggregation. r.totalActualCost is the same
    // Actual Cost figure already used throughout this row.
    costVariance: (r.earnedValue || 0) - (r.totalActualCost || 0),
  }
}

// Discover which resource types actually exist in reportData.directRows —
// no hardcoded list. Preserves the conventional Material/Labour/
// Subcontract/Equipment order first (for a stable, familiar report layout),
// then appends any other resource_type values found, alphabetically.
function discoverResourceTypes(reportData) {
  const found = new Set()
  for (const parent of (reportData.directRows || [])) {
    for (const res of (parent.resourceRows || [])) {
      if (res.resource_type) found.add(res.resource_type)
    }
  }
  const known = RESOURCE_TYPE_ORDER.filter(t => found.has(t))
  const extra = [...found].filter(t => !RESOURCE_TYPE_ORDER.includes(t)).sort()
  return [...known, ...extra]
}

// Builds one Direct resource-type section (Material/Labour/Subcontract/
// Equipment/or any other resource_type present) — identical filtering
// rule to the screen's own activeDirectRows/activeTotals computation for
// a Direct tab: a Cost Code is included if it has at least one Resource
// Code of this type; only that type's Resource Codes (and their Sub
// Codes) are printed under it.
function buildDirectResourceSection(reportData, resourceType, monthlyActualCost) {
  const directRows = reportData.directRows || []
  const matchingParents = directRows.filter(parent => (parent.resourceRows || []).some(r => r.resource_type === resourceType))

  const totals = emptySumTotals()
  for (const parent of matchingParents) {
    const matchingResources = (parent.resourceRows || []).filter(r => r.resource_type === resourceType)
    for (const res of matchingResources) {
      for (const k of SUM_FIELDS) totals[k] = (totals[k] || 0) + (res[k] || 0)
    }
  }
  totals.varianceEac = (totals.eac || 0) - (totals.prevEac || 0)
  totals.varianceCompletion = (totals.bac || 0) - (totals.eac || 0)
  // cpi/costVariance — derived from the rolled-up earnedValue/
  // totalActualCost, exactly the same formula buildCostControlData.js
  // itself uses for its own parent/grand totals (cpi = earnedValue /
  // totalActualCost when both are non-zero; never summed/averaged
  // directly, since CPI is a ratio, not an additive quantity).
  totals.cpi = (totals.earnedValue && totals.totalActualCost) ? (totals.earnedValue / totals.totalActualCost) : 0
  totals.costVariance = (totals.earnedValue || 0) - (totals.totalActualCost || 0)
  // Movement total — sum of each Resource's own Movement, read directly
  // from monthlyActualCost.byResourceCode (built by
  // buildMonthlyActualCostData() itself — see monthlyActualCost.js). No
  // aggregation happens in this file: byResourceCode is a pre-computed
  // index in the build layer, exactly like byCostCode/bySubCode.
  totals.actualThisPeriod = matchingParents
    .flatMap(parent => (parent.resourceRows || []).filter(r => r.resource_type === resourceType))
    .reduce((s, res) => s + (monthlyActualCost?.byResourceCode?.[res.code]?.movement ?? 0), 0)
  totals._isAggregateTotal = true

  const rows = []
  for (const parent of matchingParents) {
    const matchingResources = (parent.resourceRows || []).filter(r => r.resource_type === resourceType)
    if (matchingResources.length === 0) continue
    rows.push({ level: 'costcode', code: parent.code, description: parent.description, indent: 0, isGroupHeader: true, ...pickDisplayFields(parent, monthlyActualCost) })
    for (const res of matchingResources) {
      // Resource rows read Movement from monthlyActualCost.byResourceCode
      // directly (matched by res.code) — the same pre-built index used
      // above for the section total, not a re-aggregation in this file.
      rows.push({ level: 'resource', code: res.code, description: res.description, indent: 1, ...pickDisplayFields({ ...res, actualThisPeriod: monthlyActualCost?.byResourceCode?.[res.code]?.movement ?? 0, _isAggregateTotal: true }, monthlyActualCost) })
      const resSubCodes = (parent.children || []).filter(c => c.isUnderResource && c.parent_id === res.id)
      for (const sub of resSubCodes) {
        rows.push({ level: 'subcode', code: sub.code, description: sub.description, indent: 2, ...pickDisplayFields(sub, monthlyActualCost) })
      }
    }
  }

  return {
    key: resourceType,
    label: RESOURCE_TYPE_LABELS[resourceType] || (resourceType.charAt(0).toUpperCase() + resourceType.slice(1)),
    icon: 'direct',
    rows,
    totals: pickDisplayFields(totals, monthlyActualCost),
  }
}

// Builds an Indirect section — either the single 'indirect' fallback
// (no categories defined on the project), a real user-defined category
// (e.g. "Equipment & Transportation"), or the 'Uncategorized' bucket.
// Source data is read exactly as reportData already grouped it — no
// re-grouping logic here.
function buildIndirectSection(reportData, sectionKey, monthlyActualCost) {
  let indirectRows = []
  let label = 'Indirect Works'
  let totals = reportData.indirectTotals || emptySumTotals()

  if (sectionKey === 'indirect') {
    indirectRows = reportData.indirectRows || []
    totals = reportData.indirectTotals || emptySumTotals()
  } else if (sectionKey === 'cat:uncategorized') {
    indirectRows = reportData.uncategorizedIndirectRows || []
    label = 'Uncategorized'
    totals = reportData.uncategorizedTotals || emptySumTotals()
  } else if (sectionKey.startsWith('cat:')) {
    const catId = sectionKey.slice(4)
    const cat = (reportData.indirectCategories || []).find(c => c.id === catId)
    indirectRows = cat?.rows || []
    label = cat?.name || 'Category'
    totals = cat?.totals || emptySumTotals()
  }

  // Movement total for this Indirect section — sum of each top-level Cost
  // Code's own Movement (byCostCode, since Indirect rows are Cost Code →
  // Sub Code directly, with no intermediate Resource level), not the
  // stale totals.actualThisPeriod that may already exist on
  // reportData.indirectTotals/cat.totals from buildCostControlData.js.
  const indirectMovementTotal = indirectRows.reduce((s, parent) =>
    s + (monthlyActualCost?.byCostCode?.[parent.code]?.movement ?? monthlyActualCost?.bySubCode?.[parent.code]?.movement ?? 0), 0)
  totals = { ...totals, actualThisPeriod: indirectMovementTotal, _isAggregateTotal: true }

  const rows = []
  for (const parent of indirectRows) {
    rows.push({ level: 'costcode', code: parent.code, description: parent.description, indent: 0, isGroupHeader: true, ...pickDisplayFields(parent, monthlyActualCost) })
    for (const child of (parent.children || [])) {
      rows.push({ level: 'subcode', code: child.code, description: child.description, indent: 1, ...pickDisplayFields(child, monthlyActualCost) })
    }
  }

  return { key: sectionKey, label, icon: 'indirect', rows, totals: pickDisplayFields(totals, monthlyActualCost) }
}

// Builds the list of every Indirect section key present on reportData, in
// display order: each real category (by its own sort_order, already
// applied by buildReport()), then Uncategorized last if it has rows. If
// the project has zero categories defined, falls back to the single
// 'indirect' key — identical to the screen's own tab-list logic.
function discoverIndirectSectionKeys(reportData) {
  const categories = reportData.indirectCategories || []
  if (categories.length === 0) return ['indirect']
  const keys = categories.map(c => `cat:${c.id}`)
  if ((reportData.uncategorizedIndirectRows || []).length > 0) keys.push('cat:uncategorized')
  return keys
}

// Builds the always-last Summary section: a project-wide roll-up table
// (Direct Works Total / each Direct resource type / Indirect Works Total
// or each category / GRAND TOTAL), sourced exactly from the same total
// objects the screen's own SummaryView already reads — no recomputation.
function buildSummarySection(reportData, resourceTypesFound, monthlyActualCost) {
  const rt = reportData.resourceTypeTotals || {}
  const directTotals = reportData.directTotals || emptySumTotals()
  const indirectTotals = reportData.indirectTotals || emptySumTotals()
  const grandTotals = reportData.grandTotals || emptySumTotals()
  const hasCategories = (reportData.indirectCategories || []).length > 0
  const directRows = reportData.directRows || []
  const indirectRows = reportData.indirectRows || []

  // Movement for a whole resource type (e.g. "Material" across every Cost
  // Code) — sum of each matching Resource's own Movement, at Resource
  // granularity (same principle as buildDirectResourceSection's totals).
  function movementForResourceType(type) {
    return directRows
      .flatMap(parent => (parent.resourceRows || []).filter(r => r.resource_type === type))
      .reduce((s, res) => s + (monthlyActualCost?.byResourceCode?.[res.code]?.movement ?? monthlyActualCost?.bySubCode?.[res.code]?.movement ?? monthlyActualCost?.byCostCode?.[res.code]?.movement ?? 0), 0)
  }
  // Movement across all Direct Cost Codes (every resource type combined).
  function movementForAllDirect() {
    return directRows
      .flatMap(parent => parent.resourceRows || [])
      .reduce((s, res) => s + (monthlyActualCost?.byResourceCode?.[res.code]?.movement ?? monthlyActualCost?.bySubCode?.[res.code]?.movement ?? monthlyActualCost?.byCostCode?.[res.code]?.movement ?? 0), 0)
  }
  // Movement across a given list of Indirect Cost Code roots (Cost Code
  // level only — Indirect has no intermediate Resource level).
  function movementForIndirectRows(rows) {
    return rows.reduce((s, parent) =>
      s + (monthlyActualCost?.byCostCode?.[parent.code]?.movement ?? monthlyActualCost?.bySubCode?.[parent.code]?.movement ?? 0), 0)
  }

  const rows = []
  for (const type of resourceTypesFound) {
    rows.push({
      level: 'summaryrow', code: '', description: RESOURCE_TYPE_LABELS[type] || type,
      indent: 1, ...pickDisplayFields({ ...(rt[type] || emptySumTotals()), actualThisPeriod: movementForResourceType(type), _isAggregateTotal: true }),
    })
  }
  rows.push({ level: 'summarytotal', code: '', description: 'Direct Works Total', indent: 0,
    ...pickDisplayFields({ ...directTotals, actualThisPeriod: movementForAllDirect(), _isAggregateTotal: true }) })

  if (hasCategories) {
    for (const cat of (reportData.indirectCategories || [])) {
      rows.push({ level: 'summaryrow', code: '', description: cat.name, indent: 1,
        ...pickDisplayFields({ ...(cat.totals || emptySumTotals()), actualThisPeriod: movementForIndirectRows(cat.rows || []), _isAggregateTotal: true }) })
    }
    if ((reportData.uncategorizedIndirectRows || []).length > 0) {
      rows.push({ level: 'summaryrow', code: '', description: 'Uncategorized', indent: 1,
        ...pickDisplayFields({ ...(reportData.uncategorizedTotals || emptySumTotals()), actualThisPeriod: movementForIndirectRows(reportData.uncategorizedIndirectRows), _isAggregateTotal: true }) })
    }
  } else {
    rows.push({ level: 'summaryrow', code: '', description: 'Indirect Works', indent: 1,
      ...pickDisplayFields({ ...indirectTotals, actualThisPeriod: movementForIndirectRows(indirectRows), _isAggregateTotal: true }) })
  }
  rows.push({ level: 'summarytotal', code: '', description: 'Indirect Works Total', indent: 0,
    ...pickDisplayFields({ ...indirectTotals, actualThisPeriod: movementForIndirectRows(indirectRows), _isAggregateTotal: true }) })

  // Grand Total row — per explicit requirement, this must be the EXACT
  // same figure Executive Report's Grand Total KPI shows (monthly.
  // grandMovement), not a sum re-derived here from the section rows
  // above. Single shared source of truth for the project-wide "This
  // Period" figure.
  return {
    key: 'summary', label: 'Summary', icon: 'summary', isSummary: true,
    rows, totals: pickDisplayFields({ ...grandTotals, actualThisPeriod: monthlyActualCost?.grandMovement || 0, _isAggregateTotal: true }),
  }
}

export function buildCostControlPdfModel(reportData, options = {}) {
  const {
    sections = null,        // explicit list of section keys, in order — or null/omitted for "all"
    allSections = sections == null,
    projectName = '',
    orgName = '',
    monthLabel = '',
    year = '',
    generatedBy = '',
    dataSourceLabel = 'Current (Live)',
    releaseInfo = null, // { releaseNumber, forecastPeriodLabel, directRevisionLabel, indirectRevisionLabel } — only when Release
    contractValue = 0,
    logoUrl = null,
    includeCoverPage = true,
    // summary — the EXACT `summary` state object CostControl.jsx already
    // built its own on-screen Executive Cards from (setSummary(...) fed by
    // grandTotals inside buildReport()). This is the single shared source
    // of truth for every Executive Summary figure: this function performs
    // NO independent arithmetic on grandTotals for these cards — it only
    // re-labels the same fields the screen already computed and is
    // already showing, so the PDF card values can never drift from the
    // screen's card values. Required — if omitted, the Executive Summary
    // section is left empty rather than silently recomputed from
    // grandTotals under a different formula.
    summary = null,
    // visibleCols — the exact column-visibility flags from CostControl.jsx's
    // own screen state ({ commitment, accrual, varEAC, ev, cpi }). Passed
    // straight through for the renderer to decide which optional detail
    // columns to print — no filtering or interpretation happens here, this
    // function only forwards it unchanged. Omitted (null) means "show all
    // optional columns" — used by the full multi-section export.
    visibleCols = null,
    includeExecutiveSummary = true,
    // Cost Control Report Configuration (Release Management → Reports tab)
    // — plain display metadata only, resolved and merged in by
    // exportCostControlPdf.js from cost_control_report_config. No
    // financial meaning whatsoever; simply forwarded onto header for the
    // renderer to print.
    clientName = '',
    consultantName = '',
    orgWebsite = '',
    footerText = '',
    confidentialLabel = 'Strictly Confidential — Top Management Only',
    showPrintDate = true,
    showGeneratedBy = true,
    // monthlyActualCost — the output of buildMonthlyActualCostData(), the
    // SAME shared source Executive Report already reads its own "This
    // Period" (Movement) figures from. Passed straight through from
    // exportCostControlPdf.js; this file performs no calculation on it
    // beyond simple lookups by code (see pickDisplayFields above). If
    // omitted, actualThisPeriod falls back to 0 everywhere rather than
    // silently reverting to reportData's old "selected calendar month"
    // figure — the two report layers must never re-diverge onto
    // different definitions of "This Period".
    monthlyActualCost = null,
  } = options

  const resourceTypesFound = discoverResourceTypes(reportData)

  const header = {
    projectName,
    orgName,
    reportTitle: 'Detailed Cost Control Report',
    reportDate: `${monthLabel} ${year}`,
    generatedDate: new Date().toLocaleDateString('en-US', { day: '2-digit', month: 'long', year: 'numeric' }),
    generatedBy,
    dataSourceLabel,
    releaseInfo,
    logoUrl,
    clientName,
    consultantName,
    orgWebsite,
    footerText,
    confidentialLabel,
    showPrintDate,
    showGeneratedBy,
  }

  // Executive Summary — sourced directly and only from `summary` (the same
  // object CostControl.jsx's on-screen Executive Cards read: totalBudget,
  // totalCommitment, totalActual, totalAccrual, totalCashOut, totalEAC),
  // plus `contractValue`, exactly as the screen's own inline card list
  // computes ETC/Gross Profit/GP% (see CostControl.jsx's Executive Cards
  // block: fmtK(summary.totalEAC - summary.totalActual) for ETC,
  // fmtK(contractValue - summary.totalEAC) for Gross Profit,
  // ((contractValue - summary.totalEAC) / contractValue * 100) for GP%).
  // These three are the SAME formula already used on-screen, applied to
  // the SAME summary object — not a re-derivation from grandTotals.
  const s = summary || {}
  const totalEAC = s.totalEAC || 0
  const totalActual = s.totalActual || 0
  const executiveSummary = {
    bac: s.totalBudget || 0,
    commitment: s.totalCommitment || 0,
    actual: totalActual,
    accrual: s.totalAccrual || 0,
    cashOut: s.totalCashOut || 0,
    eac: totalEAC,
    etc: totalEAC - totalActual,
    varianceCompletion: (s.totalBudget || 0) - totalEAC,
    contractValue,
    grossProfit: contractValue - totalEAC,
    grossProfitPct: contractValue > 0 ? ((contractValue - totalEAC) / contractValue) * 100 : 0,
    // cpi — NOT derived from `summary` (which carries no CPI field at
    // all). Read directly from reportData.grandTotals.cpi — the exact
    // same field Executive Report's own CPI card reads
    // (cumulative.grandTotals.cpi, i.e. `g.cpi` in ExecutiveReport.jsx).
    // buildCostControlData.js already computes this as
    // earnedValue/totalActualCost at the grand-total level; this file
    // performs no calculation of its own.
    cpi: reportData.grandTotals?.cpi || 0,
  }


  // Resolve which section keys to build, in order.
  let sectionKeys
  if (allSections) {
    sectionKeys = [
      ...resourceTypesFound,
      ...discoverIndirectSectionKeys(reportData),
      'summary',
    ]
  } else {
    sectionKeys = sections
  }

  const builtSections = []
  for (const key of sectionKeys) {
    if (key === 'summary') {
      builtSections.push(buildSummarySection(reportData, resourceTypesFound, monthlyActualCost))
    } else if (resourceTypesFound.includes(key) || RESOURCE_TYPE_LABELS[key]) {
      const section = buildDirectResourceSection(reportData, key, monthlyActualCost)
      if (section.rows.length > 0 || !allSections) builtSections.push(section)
    } else if (key === 'indirect' || key.startsWith('cat:')) {
      const section = buildIndirectSection(reportData, key, monthlyActualCost)
      if (section.rows.length > 0 || !allSections) builtSections.push(section)
    }
  }

  return {
    header,
    includeCoverPage,
    includeExecutiveSummary,
    executiveSummary,
    sections: builtSections,
    visibleCols,
  }
}
